import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Trash2, Image, Video, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import FileUploader from '../../components/admin/FileUploader';
import LanguageTabs, { useAvailableLanguages } from '../../components/admin/LanguageTabs';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminHero = () => {
  const { getAuthHeader } = useAdminAuth();
  const { languages, loading: languagesLoading } = useAvailableLanguages();
  const [heroes, setHeroes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingHero, setEditingHero] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('pl');

  // Initialize empty hero with all available languages
  const getEmptyHero = () => {
    const titleObj = {};
    const subtitleObj = {};
    languages.forEach(lang => {
      titleObj[lang.code] = '';
      subtitleObj[lang.code] = '';
    });
    return {
      mediaType: 'image',
      mediaUrl: '',
      title: titleObj,
      subtitle: subtitleObj,
      buttons: [],
      scheduledFrom: '',
      scheduledTo: '',
      sortOrder: 0,
      isActive: true
    };
  };

  useEffect(() => {
    fetchHeroes();
  }, []);

  const fetchHeroes = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/hero?all=true`, {
        headers: getAuthHeader()
      });
      setHeroes(response.data);
    } catch (error) {
      console.error('Failed to fetch heroes:', error);
      toast.error('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/api/admin/hero`, editingHero, {
          headers: getAuthHeader()
        });
        toast.success('Hero utworzony');
      } else {
        await axios.put(`${API}/api/admin/hero/${editingHero.hero_id}`, editingHero, {
          headers: getAuthHeader()
        });
        toast.success('Hero zaktualizowany');
      }
      setEditingHero(null);
      setIsCreating(false);
      fetchHeroes();
    } catch (error) {
      toast.error('Nie udało się zapisać');
    }
  };

  const handleDelete = async (heroId) => {
    if (!confirm('Czy na pewno chcesz usunąć ten element?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/hero/${heroId}`, {
        headers: getAuthHeader()
      });
      toast.success('Hero usunięty');
      fetchHeroes();
    } catch (error) {
      toast.error('Nie udało się usunąć');
    }
  };

  const addButton = () => {
    const textObj = {};
    languages.forEach(lang => { textObj[lang.code] = ''; });
    setEditingHero({
      ...editingHero,
      buttons: [...(editingHero.buttons || []), { text: textObj, url: '', openInNewTab: false }]
    });
  };

  const updateButton = (index, field, value, langCode = null) => {
    const newButtons = [...editingHero.buttons];
    if (field === 'text' && langCode) {
      newButtons[index].text = {
        ...newButtons[index].text,
        [langCode]: value
      };
    } else {
      newButtons[index][field] = value;
    }
    setEditingHero({ ...editingHero, buttons: newButtons });
  };

  const removeButton = (index) => {
    const newButtons = editingHero.buttons.filter((_, i) => i !== index);
    setEditingHero({ ...editingHero, buttons: newButtons });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Zarządzanie Hero</h1>
          <p className="text-gray-400">Główny baner na stronie głównej</p>
        </div>
        <Button
          onClick={() => { setEditingHero(getEmptyHero()); setIsCreating(true); setActiveTab('pl'); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Dodaj Hero
        </Button>
      </div>

      {/* Hero List */}
      <div className="grid gap-4">
        {heroes.map((hero) => (
          <Card key={hero.hero_id} className="bg-white/5 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-32 h-20 bg-white/10 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                  {hero.mediaType === 'video' ? (
                    <Video className="w-8 h-8 text-gray-500" />
                  ) : hero.mediaUrl ? (
                    <img src={hero.mediaUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Image className="w-8 h-8 text-gray-500" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium truncate">
                      {hero.title?.pl || hero.title?.en || 'Bez tytułu'}
                    </h3>
                    {!hero.isActive && (
                      <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                        Nieaktywny
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm truncate">
                    {hero.subtitle?.pl || hero.subtitle?.en || 'Brak opisu'}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Kolejność: {hero.sortOrder || 0}
                    {hero.scheduledFrom && ` | Od: ${hero.scheduledFrom.split('T')[0]}`}
                    {hero.scheduledTo && ` | Do: ${hero.scheduledTo.split('T')[0]}`}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { setEditingHero(hero); setIsCreating(false); }}
                    className="text-gray-400 hover:text-white"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(hero.hero_id)}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {heroes.length === 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 text-center">
              <Image className="w-12 h-12 mx-auto text-gray-500 mb-4" />
              <p className="text-gray-400">Brak elementów Hero</p>
              <Button
                onClick={() => { setEditingHero(getEmptyHero()); setIsCreating(true); setActiveTab('pl'); }}
                variant="outline"
                className="mt-4"
              >
                Dodaj pierwszy element
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Modal */}
      {editingHero && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10">
              <CardTitle className="text-white">
                {isCreating ? 'Nowy Hero' : 'Edytuj Hero'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingHero(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Media Type */}
              <div className="space-y-2">
                <Label className="text-gray-300">Typ mediów</Label>
                <div className="flex gap-4">
                  <Button
                    variant={editingHero.mediaType === 'image' ? 'default' : 'outline'}
                    onClick={() => setEditingHero({ ...editingHero, mediaType: 'image' })}
                    className={editingHero.mediaType === 'image' ? 'bg-purple-600' : ''}
                  >
                    <Image className="w-4 h-4 mr-2" />
                    Obraz
                  </Button>
                  <Button
                    variant={editingHero.mediaType === 'video' ? 'default' : 'outline'}
                    onClick={() => setEditingHero({ ...editingHero, mediaType: 'video' })}
                    className={editingHero.mediaType === 'video' ? 'bg-purple-600' : ''}
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Wideo
                  </Button>
                </div>
              </div>

              {/* Media URL */}
              <div className="space-y-2">
                <Label className="text-gray-300">
                  {editingHero.mediaType === 'video' ? 'Wideo' : 'Obraz'}
                </Label>
                <FileUploader
                  accept={editingHero.mediaType === 'video' ? 'video/*' : 'image/*'}
                  currentUrl={editingHero.mediaUrl}
                  onUpload={(url) => setEditingHero({ ...editingHero, mediaUrl: url })}
                />
                <p className="text-gray-500 text-xs">lub wklej URL:</p>
                <Input
                  value={editingHero.mediaUrl || ''}
                  onChange={(e) => setEditingHero({ ...editingHero, mediaUrl: e.target.value })}
                  placeholder="https://..."
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Language Tabs for Title/Subtitle */}
              <div className="space-y-4">
                <LanguageTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  showIndicator={true}
                  hasContent={languages.reduce((acc, lang) => {
                    acc[lang.code] = !!(editingHero.title?.[lang.code] || editingHero.subtitle?.[lang.code]);
                    return acc;
                  }, {})}
                />

                {languages.map(lang => (
                  activeTab === lang.code && (
                    <div key={lang.code} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300">Tytuł ({lang.nativeName})</Label>
                        <Input
                          value={editingHero.title?.[lang.code] || ''}
                          onChange={(e) => setEditingHero({ 
                            ...editingHero, 
                            title: { ...editingHero.title, [lang.code]: e.target.value } 
                          })}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Podtytuł ({lang.nativeName})</Label>
                        <Input
                          value={editingHero.subtitle?.[lang.code] || ''}
                          onChange={(e) => setEditingHero({ 
                            ...editingHero, 
                            subtitle: { ...editingHero.subtitle, [lang.code]: e.target.value } 
                          })}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                    </div>
                  )
                ))}
              </div>

              {/* Buttons */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300">Przyciski</Label>
                  <Button variant="outline" size="sm" onClick={addButton}>
                    <Plus className="w-4 h-4 mr-1" /> Dodaj przycisk
                  </Button>
                </div>
                {editingHero.buttons?.map((btn, idx) => (
                  <div key={idx} className="p-3 bg-white/5 rounded-lg space-y-3">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                      {languages.map(lang => (
                        <Input
                          key={lang.code}
                          value={btn.text?.[lang.code] || ''}
                          onChange={(e) => updateButton(idx, 'text', e.target.value, lang.code)}
                          placeholder={`Tekst (${lang.code.toUpperCase()})`}
                          className="bg-white/5 border-white/10 text-white text-sm"
                        />
                      ))}
                    </div>
                    <div className="flex gap-2 items-center">
                      <Input
                        value={btn.url || ''}
                        onChange={(e) => updateButton(idx, 'url', e.target.value)}
                        placeholder="URL"
                        className="bg-white/5 border-white/10 text-white text-sm flex-1"
                      />
                      <label className="flex items-center gap-2 text-gray-300 text-sm whitespace-nowrap cursor-pointer">
                        <input
                          type="checkbox"
                          checked={btn.openInNewTab || false}
                          onChange={(e) => updateButton(idx, 'openInNewTab', e.target.checked)}
                          className="rounded border-white/20 bg-white/5 text-purple-600 focus:ring-purple-500"
                        />
                        Nowe okno
                      </label>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeButton(idx)}
                        className="text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Scheduling */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Widoczny od</Label>
                  <Input
                    type="datetime-local"
                    value={editingHero.scheduledFrom ? editingHero.scheduledFrom.slice(0, 16) : ''}
                    onChange={(e) => setEditingHero({ ...editingHero, scheduledFrom: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Widoczny do</Label>
                  <Input
                    type="datetime-local"
                    value={editingHero.scheduledTo ? editingHero.scheduledTo.slice(0, 16) : ''}
                    onChange={(e) => setEditingHero({ ...editingHero, scheduledTo: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Sort Order & Active */}
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Label className="text-gray-300">Kolejność</Label>
                  <Input
                    type="number"
                    value={editingHero.sortOrder || 0}
                    onChange={(e) => setEditingHero({ ...editingHero, sortOrder: parseInt(e.target.value) })}
                    className="bg-white/5 border-white/10 text-white w-24"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-gray-300">Aktywny</Label>
                  <Switch
                    checked={editingHero.isActive}
                    onCheckedChange={(checked) => setEditingHero({ ...editingHero, isActive: checked })}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingHero(null); setIsCreating(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminHero;
